import { DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import { Button, Drawer, Form, Input, message, Progress, Upload } from 'antd';
import { post } from 'core/fetch';
import { useScreen } from 'hooks/useScreen';
import { useEffect, useState } from 'react';

import { queryErrorMessage } from '../CashTransactions/types';
import AppIcon from './AppIcon';
import type { MobileAppInfo } from './types';

// The file /mobile_app/storage/icon answers with; stored as the app's icon.
interface UploadedFile {
  name: string;
  fileKey: string;
  url: string;
  mimetype?: string;
  isPublic?: boolean;
}

interface FormValues {
  name: string;
  code: string;
  description?: string;
}

interface Props {
  open: boolean;
  // null = a new app.
  app: MobileAppInfo | null;
  onClose: () => void;
  onSaved: () => void;
}

// Adds or edits a Mobile app over the cards. The icon uploads at once; the app
// is saved (mobile_app_save) with Хадгалах.
export default function MobileAppFormDrawer({ open, app, onClose, onSaved }: Props) {
  const { runQuery } = useScreen();
  const [form] = Form.useForm<FormValues>();
  // undefined = unchanged, null = removed.
  const [icon, setIcon] = useState<UploadedFile | null | undefined>(undefined);
  const [progress, setProgress] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      name: app?.name ?? '',
      code: app?.code ?? '',
      description: app?.description ?? '',
    });
    setIcon(undefined);
    setProgress(null);
  }, [open, app, form]);

  const iconUrl = icon === undefined ? (app?.icon_url ?? null) : (icon?.url ?? null);

  const upload = async (file: File) => {
    const data = new FormData();
    data.append('files', file);
    setProgress(0);
    try {
      const uploaded = await post<UploadedFile>(
        '/mobile_app/storage/icon',
        data,
        { 'Content-Type': 'multipart/form-data' },
        (e) => setProgress(Math.round((95 * e.loaded) / (e.total || 1))),
      );
      setIcon(uploaded);
    } catch (e) {
      message.error(queryErrorMessage(e) || 'Зураг хуулахад алдаа гарлаа.');
    } finally {
      setProgress(null);
    }
  };

  const save = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      await runQuery('mobile_app_save', {
        ...(app ? { id: app.id } : {}),
        name: values.name.trim(),
        code: values.code.trim(),
        description: values.description?.trim() || null,
        ...(icon !== undefined ? { icon } : {}),
      });
      message.success(app ? 'Хадгаллаа' : 'Апп нэмэгдлээ');
      onSaved();
    } catch (e) {
      message.error(queryErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={app ? app.name : 'Апп нэмэх'}
      size={520}
      destroyOnHidden
      extra={
        <Button type="primary" loading={saving} disabled={progress !== null} onClick={save}>
          Хадгалах
        </Button>
      }
    >
      <Form form={form} layout="vertical" requiredMark onFinish={save}>
        <Form.Item label="Дүрс">
          <div className="flex items-center gap-4">
            <div className="shrink-0 rounded-2xl shadow-sm ring-1 ring-black/5">
              <AppIcon url={iconUrl} size={72} />
            </div>
            <div className="min-w-0 flex-1">
              <Upload.Dragger
                accept="image/png, image/jpeg"
                showUploadList={false}
                maxCount={1}
                beforeUpload={(file) => {
                  upload(file);
                  return false;
                }}
                disabled={progress !== null}
              >
                <p className="ant-upload-drag-icon mb-1!">
                  <InboxOutlined />
                </p>
                <p className="text-sm text-gray-500">
                  {iconUrl ? 'Солих бол дарах эсвэл чирэх' : 'Зураг сонгох эсвэл чирэх'}
                </p>
                <p className="text-xs text-gray-400">PNG, JPG · дөрвөлжин</p>
              </Upload.Dragger>
              {progress !== null && <Progress percent={progress} size="small" />}
              {iconUrl && progress === null && (
                <Button
                  type="link"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  className="mt-1 px-0!"
                  onClick={() => setIcon(null)}
                >
                  Дүрс устгах
                </Button>
              )}
            </div>
          </div>
        </Form.Item>
        <Form.Item
          name="name"
          label="Нэр"
          rules={[{ required: true, whitespace: true, message: 'Нэрээ оруулна уу' }]}
        >
          <Input placeholder="Edulogy - Цайны газар апп" />
        </Form.Item>
        <Form.Item
          name="code"
          label="Код"
          extra="Хувилбар нэмэх API-д ашиглана."
          rules={[
            { required: true, whitespace: true, message: 'Кодоо оруулна уу' },
            {
              pattern: /^[a-z0-9-]+$/,
              message: 'Жижиг латин үсэг, тоо, зураас (-) ашиглана',
            },
          ]}
        >
          <Input placeholder="tuck-shop" className="font-mono" />
        </Form.Item>
        <Form.Item name="description" label="Тайлбар">
          <Input.TextArea rows={6} placeholder="Апп юунд зориулагдсан, ямар боломжтой вэ" />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
